import { debounce } from '@repo/utils/debounce'
import { extractClientMatterNumbers } from '@repo/utils/clientMatterNumbers'
import { convertDateToLocalTimeInIsoFormat, formatDate } from '@repo/utils/time'
import { getClientNoAndMatterNoFromCookies } from '@repo/utils/reactIframeLoop'
import { mixpanelTrackUserEvent } from '@repo/app-assets/Mixpanel'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { ospreyRecentMattersSchema, ospreyMatterSchema, ospreySearchSchema } from '@repo/validation/schema_ospreyAPI'
import { storeWrapper } from '../storeWrapper'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useClientStore } from '@repo/store/useClientStore'
import { useNewMatterStore } from '../useNewMatterStore/useNewMatterStore'
import { useUrlStore } from '../useUrlStore/useUrlStore'
import Cookies from 'js-cookie'

const initialState = {
  matters: {},
  currentMatter: null,
  currentClientMatterNo: null,
  recentMatters: [],
  matterSearchResults: [],
  matterDrawerIsOpen: false,
  matterDrawerClientNo: null,
  matterDrawerClientMatterNo: null,
  matterDrawerConflicts: null,
  relatedMatters: [],
  matterNotes: {},
  matterNotesIsOpen: false
}

let latestSearchCriteria = ''
let searchQueryStatus = {}

export const useMattersStore = storeWrapper('useMattersStore', (set, get) => ({
  ...initialState,
  setCurrentMatter: ({ clientMatterNo, ospreyMatter_DI }) => {
    if (!clientMatterNo) {
      set({ currentMatter: null, currentClientMatterNo: null })
      return
    }

    const {
      clientNo,
      matterNo,
      clientMatterNo: decodedClientMatterNo
    } = useUrlStore.getState().urlDecodeClientMatterNo({ clientMatterNoParam: clientMatterNo }) || {}

    const prevClientMatterNo = get().currentClientMatterNo
    const currentMatter = get().matters[decodedClientMatterNo] || null

    if (currentMatter?.isUserRestricted) {
      throw new Error('You are not authorised to access this matter', {
        cause: { status: 403 }
      })
    }

    // set the cookie value, so we don't have React arguing with the iframe about the current matter
    Cookies.set('LastCliMatt', `clientNo=${clientNo}&matterNo=${matterNo}`, { expires: 365 })
    set({ currentMatter, currentClientMatterNo: decodedClientMatterNo })

    const matterHasChanged = prevClientMatterNo !== decodedClientMatterNo
    const alreadyHaveExtendedMatter = currentMatter?.remunerationTypeId && currentMatter?.dateOpened

    // fetch the new matter details if necessary
    if (matterHasChanged || !alreadyHaveExtendedMatter) {
      const { isLoggedIn } = useAuthenticationStore.getState()
      if (isLoggedIn && clientMatterNo) {
        get().fetchMatter({ clientMatterNo: decodedClientMatterNo, ospreyMatter_DI, trackEvent: true })
      }
    }
  },
  fetchMatter: async ({ clientMatterNo, ospreyMatter_DI, trackEvent = false }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchMatter-${clientMatterNo}`,
        delay: 600,
        callback: async () => {
          const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })

          // don't fetch if the clientNo or matterNo is not valid
          if (!clientNo || (!matterNo && matterNo !== 0)) {
            return
          }

          let fetchedMatter

          if (ospreyMatter_DI) {
            fetchedMatter = ospreyMatter_DI({ clientNo, matterNo })
          } else {
            try {
              fetchedMatter =
                (await ospreyAPI({
                  service: 'matters',
                  endpoint: 'Matter/GetExtended',
                  data: { ClientNo: clientNo, MatterNo: matterNo },
                  schema: ospreyMatterSchema
                })) || {}
            } catch (err) {
              if (clientMatterNo === get().currentClientMatterNo) {
                get().setCurrentMatter({ clientMatterNo: null })
              }

              return
            }

            fetchedMatter.matterDescription = fetchedMatter?.description
            fetchedMatter.clientMatterNo = clientMatterNo
            fetchedMatter.forename = fetchedMatter.clientForename
            fetchedMatter.surname = fetchedMatter.clientSurname
          }

          const existingMatters = get().matters
          const recentMatters = get().recentMatters
          const matchingRecentMatter = recentMatters?.find(
            (matter) => matter.clientNo === clientNo && Number(matter.matterNo) === Number(matterNo)
          )
          existingMatters[clientMatterNo] = { ...matchingRecentMatter, ...fetchedMatter }
          set({ matters: { ...existingMatters } })

          if (clientMatterNo === get().currentClientMatterNo) {
            set({ currentMatter: existingMatters[clientMatterNo] })
          }

          if (trackEvent) {
            get().trackMatterSelectionInMixpanel({ properties: { ...fetchedMatter } })
          }

          resolve(fetchedMatter)
        }
      })
    })
  },
  updateMatter: async ({ clientMatterNo, reviewDate, successToastMessage }) => {
    await get().fetchMatter({ clientMatterNo })

    const matter = get().matters[clientMatterNo]
    if (reviewDate) matter.reviewDate = reviewDate

    await ospreyAPI({
      service: 'matters',
      endpoint: 'Matter/Update',
      method: 'PUT',
      data: { ...matter },
      successToast: successToastMessage
        ? {
            type: 'success',
            title: `Matter updated`,
            text: successToastMessage,
            clientMatterNo
          }
        : null
    })
  },
  fetchRecentMatters: async (ospreyRecentMatters_DI) => {
    debounce({
      id: `fetchRecentMatters`,
      delay: 600,
      callback: async () => {
        // get recent matters and put them in the store
        let fetchedRecentMatters

        if (ospreyRecentMatters_DI) {
          fetchedRecentMatters = ospreyRecentMatters_DI()
        } else {
          const { rows } =
            (await ospreyAPI({
              service: 'matters',
              endpoint: 'LastMatter/GetAllExtendedByCurrentUser',
              data: { startAtIndex: 0, pageSize: 20 },
              schema: ospreyRecentMattersSchema
            })) || {}

          fetchedRecentMatters = rows.map((matter) => ({
            ...matter,
            clientMatterNo: `${matter.clientNo}-${matter.matterNo}`,
            searchTerm: `${matter.clientNo}-${matter.matterNo}${matter.forename} ${matter.surname} ${matter.matterDescription}`
          }))
        }

        set({ recentMatters: fetchedRecentMatters })

        // if we don't already have this record in the list of matters, add it
        const existingMatterIds = Object.keys(get().matters)
        const existingMatters = get().matters
        fetchedRecentMatters?.forEach((matter) => {
          const clientMatterNo = `${matter.clientNo.trim()}-${matter.matterNo}`
          if (!existingMatterIds.includes(clientMatterNo)) {
            existingMatters[clientMatterNo] = { ...existingMatters[clientMatterNo], ...matter }
          }

          if (clientMatterNo === get().currentClientMatterNo) {
            set({ currentMatter: existingMatters[clientMatterNo] })
          }
        })
        set({ matters: { ...existingMatters } })
      }
    })
  },
  fetchMatterSearch: async ({ searchCriteria, pageSize = 20, includeArchived }) => {
    if (!searchCriteria) return
    latestSearchCriteria = searchCriteria
    const timestamp = Date.now()
    searchQueryStatus[timestamp] = 'pending'

    // already debounced in the UI component
    const { rows } = await ospreyAPI({
      service: 'matters',
      endpoint: 'Matter/GetAllDashboardMattersBySearchTerm',
      data: {
        SearchTerm: searchCriteria,
        StartAtIndex: 0,
        PageSize: pageSize,
        IncludeArchived: includeArchived ?? false
      },
      schema: ospreySearchSchema
    })

    const matterSearchResults =
      rows.map((matter) => ({
        ...matter,
        clientMatterNo: `${matter.clientNo}-${matter.matterNo}`,
        searchTerm: `${matter.clientNo}-${matter.matterNo}${matter.forename} ${matter.surname} ${matter.matterDescription}`
      })) || []

    delete searchQueryStatus[timestamp]
    const pendingQueryCount = Object.values(searchQueryStatus).length
    const searchQueriesStillInProgress = pendingQueryCount > 0

    // only return results if the supplied search criteria matches the most recent search criteria
    if (searchCriteria === latestSearchCriteria) {
      set({ matterSearchResults })
      return Promise.resolve({ matterSearchResults, searchCriteria, searchQueriesStillInProgress })
    }

    return Promise.resolve({ matterSearchResults: null, searchQueriesStillInProgress })
  },
  clearMatterSearchResults: () => {
    set({ matterSearchResults: [] })
  },
  fetchMatterByClientNo: async ({ clientNo }) => {
    if (!clientNo) return

    const { rows } = await ospreyAPI({
      service: 'matters',
      endpoint: 'Matter/GetAllExtendedByClientNo',
      data: {
        ClientNo: clientNo,
        StartAtIndex: 0,
        PageSize: 50
      }
    })

    const relatedMatters = rows
      .map((matter) => ({
        ...matter,
        forename: matter.clientForename,
        surname: matter.clientSurname,
        matterDescription: matter.description,
        clientMatterNo: `${matter.clientNo}-${matter.matterNo}`
      }))
      .sort((a, b) => {
        if (a.matterNo < b.matterNo) return 1
        return -1
      })

    set({ relatedMatters })
  },
  updateClientAndMatterFromCookies: () => {
    const { clientNo, matterNo } = getClientNoAndMatterNoFromCookies()
    const clientMatterNo = `${clientNo}-${matterNo}`

    // check if matter has changed
    if (clientNo && (matterNo || matterNo === 0)) {
      if (clientMatterNo !== get().currentClientMatterNo) {
        // matter has updated, so fetch latest list of recent matters
        get().setCurrentMatter({ clientMatterNo })
        const { setCurrentClient } = useClientStore.getState()
        setCurrentClient({ clientNo })
        get().fetchRecentMatters()
      }
    }

    return { clientNo, matterNo, clientMatterNo }
  },
  setMatterDrawerIsOpen: (bool, clientNo = null, clientMatterNo = null, matterDrawerConflicts = null) => {
    set({
      matterDrawerIsOpen: bool,
      matterDrawerClientNo: clientNo,
      matterDrawerClientMatterNo: clientMatterNo,
      matterDrawerConflicts
    })
  },
  trackMatterSelectionInMixpanel: ({ properties }) => {
    const {
      clientMatterNo,
      privateOrLegalAid,
      highRisk,
      workflowTypeDescription,
      workflowTypeId,
      dateArchived,
      dateCompleted,
      prospect,
      feeEarnerId,
      feeEarnerDescription,
      clientNo,
      matterNo
    } = properties

    mixpanelTrackUserEvent('Matter selection', {
      clientMatterNo: clientMatterNo || `${clientNo}-${matterNo}`,
      privateOrLegalAid,
      highRisk,
      workflowTypeDescription,
      workflowTypeId,
      isArchived: dateArchived || false,
      isCompleted: dateCompleted || false,
      prospect,
      feeEarnerId,
      feeEarnerDescription
    })
  },
  fetchNextMatterNo: async ({ clientNo }) => {
    const nextMatterNo = await ospreyAPI({
      service: 'matters',
      endpoint: 'Matter/GetNextMatterNo',
      data: { clientNo }
    })

    return Promise.resolve(nextMatterNo)
  },
  checkIfMatterNoExists: async ({ clientNo, matterNo }) => {
    const res = await ospreyAPI({
      service: 'matters',
      endpoint: 'Matter/Exists',
      data: { ClientNo: clientNo, MatterNo: matterNo }
    })

    return Promise.resolve(res === true)
  },
  fetchNextUFN: async ({ dateTime }) => {
    const formattedDateTime = dateTime instanceof Date ? convertDateToLocalTimeInIsoFormat(dateTime) : dateTime
    const ufn = await ospreyAPI({
      service: 'matters',
      endpoint: 'Matter/GetNextUniqueFileNumberByDate',
      data: { dateTime: formattedDateTime }
    })

    return Promise.resolve(ufn)
  },
  checkIfUFNExists: async ({ ufn, clientNo = null }) => {
    const res = await ospreyAPI({
      service: 'matters',
      endpoint: 'Matter/UniqueFileNumberExists',
      data: { uniqueFileNumber: ufn, clientNo }
    })

    return Promise.resolve(res === true)
  },
  fetchDefaultProspectValueForNewMatters: async () => {
    const res = await ospreyAPI({
      service: 'lookups',
      endpoint: 'OspreyConfiguration/GetMatterProspectDefaultValue'
    })

    useNewMatterStore.setState({ defaultProspectValue: res === true, prospect: res === true })
  },
  fetchMatterNotes: async ({ clientMatterNo }) => {
    const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })
    const res = await ospreyAPI({
      service: 'matters',
      endpoint: 'MatterNote/GetAllByClientNoAndMatterNo',
      data: { clientNo, matterNo }
    })

    res.forEach((note) => {
      note.formattedDate = formatDate(note.dateEntered, 'EEE d MMM yyyy')
    })

    set({ matterNotes: { ...get().matterNotes, [clientMatterNo]: res } })
  },
  createMatterNotes: async ({ clientMatterNo, dateEntered, notes }) => {
    const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })

    await ospreyAPI({
      service: 'matters',
      endpoint: 'MatterNote/Create',
      method: 'POST',
      data: {
        clientNo,
        matterNo,
        dateEntered,
        notes
      }
    })

    get().fetchMatterNotes({ clientMatterNo })
  },
  updateMatterNotes: async ({ clientMatterNo, matterNoteId, dateEntered, notes }) => {
    const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })

    await ospreyAPI({
      service: 'matters',
      endpoint: 'MatterNote/Update',
      method: 'PUT',
      data: {
        clientNo,
        matterNo,
        matterNoteId,
        dateEntered,
        notes
      }
    })

    get().fetchMatterNotes({ clientMatterNo })
  },
  deleteMatterNotes: async ({ clientMatterNo, matterNoteId }) => {
    await ospreyAPI({
      service: 'matters',
      endpoint: 'MatterNote/Delete',
      method: 'DELETE',
      sendAsUrlParams: true,
      data: {
        id: matterNoteId
      }
    })

    get().fetchMatterNotes({ clientMatterNo })
  },
  fetchLedgerDisbursements: async ({ clientMatterNo, currencyCode = 'GBP', billDate }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchLedgerDisbursements-${clientMatterNo}`,
        delay: 300,
        callback: async () => {
          const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })

          const res = await ospreyAPI({
            service: 'matters',
            endpoint: 'LedgerPosting/GetAllLedgerDisbursements',
            data: { clientNo, matterNo, currencyCode, billDate }
          })

          resolve(res)
        }
      })
    })
  },
  reset: () => {
    const matters = get().matters
    Object.keys(matters).forEach((key) => delete matters[key])
    set({ ...initialState, matters })
  }
}))
